import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { CircularProgress, TextField } from '@mui/material';
import { useCountdown } from '../../hooks';

const Digit = styled(TextField)(({ theme }) => ({
  width: '100%',
  maxWidth: '84px',
  maxHeight: '84px',
  '& .MuiInputBase-root': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  },
  '& input': {
    textAlign: 'center',
    fontSize: '32px',
    fontWeight: '500',
  },
}));

const NUM_VAL_EXP = /^[0-9]+$/;

/**
 * CodeValidator
 *
 * Componente para validar un código numérico (OTP), con intentos y reenvío.
 *
 * @component
 * @param {function} onResend - Callback para reenviar el código
 * @param {number} [codeLenght=4] - Longitud del código a ingresar
 * @param {number} [attempts=4] - Número de intentos permitidos
 * @param {boolean} [isLoadingResend=false] - Si está cargando el reenvío
 * @param {function} [onOverflow] - Callback al rebasar los intentos y terminar el tiempo de espera
 * @param {function} [onBlocked] - Callback al bloquearse por demasiados intentos
 * @param {function} [onChange] - Callback al cambiar el valor del código
 * @param {object} [props] - Otras props para el contenedor
 * @param {React.Ref} ref - Referencia para exponer métodos imperativos
 * @returns {JSX.Element}
 */
export const CodeValidator = forwardRef(
  (
    {
      onResend,
      codeLenght = 4,
      attempts = 4,
      isLoadingResend = false,
      onOverflow = () => {},
      onBlocked = () => {},
      onChange = () => {},
      ...props
    },
    ref
  ) => {
    const [currentCode, setCurrentCode] = useState(new Array(codeLenght).fill(''));
    const [currentAttempts, setCurrentAttempts] = useState(attempts);
    const inputRefs = useRef([]);

    const { startTimer, counter, isCounting, isOverflow } = useCountdown(60);

    const handleLoadNum = (event, position) => {
      event.preventDefault();
      const nextVal = event.target.value;
      const isNumber = NUM_VAL_EXP.test(nextVal);
      if (Number.isNaN(nextVal) || !isNumber) return;
      const nextCode = currentCode.map((item, index) =>
        index === position ? nextVal.slice(-1) : item
      );
      setCurrentCode(nextCode);

      if (event.target.value && inputRefs.current[position + 1]) {
        inputRefs.current[position + 1].focus();
      }
    };

    const handleResetCode = () => {
      const resetCode = currentCode.map((__, index) => '');
      setCurrentCode(resetCode);
    };

    const handleRemoveAttempt = () => {
      if (currentAttempts <= 0) return;
      setCurrentAttempts((current) => current - 1);
    };

    const handleResetAttempts = () => {
      setCurrentAttempts(attempts);
    };

    useEffect(() => {
      if (currentAttempts > 0) return;
      startTimer();
      onBlocked();
    }, [currentAttempts]);

    useEffect(() => {
      onChange(currentCode.join('').trim());
    }, [currentCode]);

    useEffect(() => {
      if (!isOverflow) return;
      handleResetAttempts();
      onOverflow();
    }, [isOverflow]);

    useImperativeHandle(ref, () => ({
      handleResetCode,
      handleRemoveAttempt,
    }));

    return (
      <div {...props}>
        <div className="w-full flex flex-row items-center">
          <Typography
            className="mt-8 text-center sm:text-start"
            sx={{
              color: (_theme) =>
                isCounting ? _theme.palette.text.disabled : _theme.palette.text.primary,
            }}
          >
            ¿No recibiste el código? Da click
            <Typography
              sx={{
                color: (_theme) =>
                  isCounting ? _theme.palette.text.disabled : _theme.palette.info.main,
              }}
              component="button"
              onClick={onResend}
              disabled={isCounting}
            >
              &nbsp;aquí&nbsp;
            </Typography>
            para reenviar
            {isLoadingResend && <CircularProgress size="1.4rem" className="mx-8 bottom-16" />}
          </Typography>
        </div>
        <div className="flex flex-row flex-nowrap justify-center items-center w-full my-24">
          {currentCode.map((child, position) => (
            <Digit
              disabled={isCounting}
              className="mx-8"
              key={`otp_child_render_${position}`}
              inputRef={(el) => {
                inputRefs.current[position] = el;
              }}
              type="tel"
              variant="outlined"
              color="primary"
              required
              inputProps={{ maxLength: 10 }}
              value={child}
              onChange={(e) => handleLoadNum(e, position)}
            />
          ))}
        </div>
        <div>
          {currentAttempts < 4 && currentAttempts > 0 && (
            <Typography>
              Código incorrecto. Te queda{currentAttempts > 1 ? 'n' : ''} {currentAttempts} intento
              {currentAttempts > 1 ? 's' : ''}
            </Typography>
          )}
          {isCounting && currentAttempts <= 0 && (
            <Typography>
              Rebasaste los intentos permitidos. Por favor espera{' '}
              <span className="font-bold">{counter}s</span> para intentar de nuevo.
            </Typography>
          )}
        </div>
      </div>
    );
  }
);
